import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Box, Button, Typography } from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
import { getRequest } from "../../api/apiServices";
import { getCampaignPrefill, getVonqCampaignRecord } from "../../api/apiList";
import CompaignForm from "../../components/Compaign/CompaigForm";
import { CampaignPrefillProvider } from "../../context/CampaignPrefillContext";
import { CampaignPrefillResponse } from "../../types/campaignPrefill";
import {
  buildWizardDataFromCampaignRecord,
  enrichWizardWithJobPrefill,
} from "../../utils/buildWizardDataFromCampaignRecord";
import { clearCampaignWizardStorage } from "../../utils/campaignPrefillStorage";
import Loader from "../../utils/Loading";

const CampaignEditPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [campaignName, setCampaignName] = useState("");
  const [jobSpecificationId, setJobSpecificationId] = useState<number | null>(
    null
  );
  const [prefill, setPrefill] = useState<CampaignPrefillResponse | null>(null);

  useEffect(() => {
    let isMounted = true;

    const loadCampaignForEdit = async () => {
      const recordId = Number(id);
      if (!id || Number.isNaN(recordId)) {
        setError("Invalid campaign id.");
        setIsLoading(false);
        return;
      }

      setIsLoading(true);
      setError(null);

      try {
        const response = await getRequest(getVonqCampaignRecord(recordId));
        const campaign = response?.data?.data;

        if (!isMounted) return;

        if (!campaign) {
          setError("Campaign not found.");
          setIsLoading(false);
          return;
        }

        clearCampaignWizardStorage();

        let wizardData = buildWizardDataFromCampaignRecord(campaign);
        let linkedPrefill: CampaignPrefillResponse | null = null;

        if (campaign.jobSpecificationId) {
          try {
            const prefillResponse = await getRequest(
              getCampaignPrefill(campaign.jobSpecificationId)
            );
            linkedPrefill = prefillResponse?.data?.data || null;
            if (linkedPrefill) {
              wizardData = enrichWizardWithJobPrefill(wizardData, linkedPrefill);
            }
          } catch {
            // Edit still works from stored campaign when prefill fails
          }
        }

        localStorage.setItem("CompaignData", JSON.stringify(wizardData));

        setCampaignName(campaign.campaignName || "Campaign");
        setJobSpecificationId(
          campaign.jobSpecificationId || wizardData.jobSpecificationId || null
        );
        setPrefill(linkedPrefill);
        setIsLoading(false);
      } catch {
        if (!isMounted) return;
        setError("Failed to load campaign for editing.");
        setIsLoading(false);
      }
    };

    loadCampaignForEdit();

    return () => {
      isMounted = false;
    };
  }, [id]);

  if (isLoading) {
    return <Loader />;
  }

  if (error) {
    return (
      <Box sx={{ p: 4, maxWidth: 640, mx: "auto" }}>
        <Typography variant="h5" fontWeight={700} gutterBottom>
          Cannot open campaign
        </Typography>
        <Typography color="text.secondary" sx={{ mb: 3 }}>
          {error}
        </Typography>
        <Button variant="contained" onClick={() => navigate("/campaigns")}>
          Back to campaigns
        </Button>
      </Box>
    );
  }

  return (
    <CampaignPrefillProvider
      value={{
        jobSpecificationId,
        prefill,
        eligibility: prefill?.eligibility || null,
        isLoading: false,
        error: null,
      }}
    >
      <Box sx={{ maxWidth: 1200, mx: "auto", px: 2, pt: 2 }}>
        <Box
          sx={{
            mb: 2,
            p: 2,
            borderRadius: "12px",
            bgcolor: "#eff6ff",
            border: "1px solid #bfdbfe",
            display: "flex",
            alignItems: "center",
            gap: 1,
          }}
        >
          <EditIcon sx={{ color: "#2563eb" }} />
          <Box>
            <Typography variant="body2" color="text.secondary">
              Editing campaign
            </Typography>
            <Typography fontWeight={700}>{campaignName}</Typography>
            {jobSpecificationId && (
              <Typography variant="caption" color="text.secondary">
                Linked to job specification #{jobSpecificationId}
              </Typography>
            )}
          </Box>
        </Box>
        <CompaignForm />
      </Box>
    </CampaignPrefillProvider>
  );
};

export default CampaignEditPage;
